// ============================================================================
// Busca da home — normalização, relevância e mapeamento para realce.
//
// Separado do componente porque o realce exige mapear o índice do casamento no
// texto NORMALIZADO de volta para o índice no texto ORIGINAL: a normalização
// NFD decompõe "é" em "e" + marca combinante, então os índices não coincidem.
// ============================================================================

export const MIN_QUERY_LENGTH = 3;
export const MAX_RESULTS = 10;

// Marcas combinantes separadas pelo NFD (U+0300–U+036F). Construída via
// RegExp para o intervalo ficar como escape ASCII no fonte, e não como os
// próprios caracteres combinantes (que somem em qualquer re-encoding).
const COMBINING_MARKS = /[\u0300-\u036f]/g;

// Ordem de relevância (menor = mais relevante).
const RANK_EXACT = 0; // rótulo idêntico ao termo
const RANK_PREFIX = 1; // rótulo começa com o termo
const RANK_WORD_PREFIX = 2; // alguma palavra do rótulo começa com o termo
const RANK_CONTAINS = 3; // termo aparece no meio de uma palavra
const RANK_MODULE = 4; // não casou o rótulo, casou o nome do módulo

/**
 * Normaliza texto para comparação: sem acento, sem caixa.
 */
export function normalizeText(text) {
   return (text || '')
      .normalize('NFD')
      .replace(COMBINING_MARKS, '')
      .toLowerCase();
}

/**
 * Normaliza preservando um mapa de índices para o texto original.
 *
 * Normaliza caractere a caractere: cada caractere original pode virar 0, 1 ou
 * mais caracteres normalizados, e `map[i]` guarda de qual índice do original
 * veio o i-ésimo caractere normalizado. Sem isso, "Roteirização" normalizada
 * ("roteirizacao") teria índices deslocados e o realce marcaria o trecho errado.
 *
 * @returns {{ norm: string, map: number[] }}
 */
export function buildNormalizedIndex(text) {
   const source = text || '';
   const map = [];
   let norm = '';

   for (let i = 0; i < source.length; i += 1) {
      const piece = normalizeText(source[i]);
      for (let k = 0; k < piece.length; k += 1) map.push(i);
      norm += piece;
   }

   return { norm, map };
}

/**
 * Converte um casamento no texto normalizado para um intervalo no original.
 *
 * @returns {{ start: number, end: number } | null}
 */
function toOriginalRange({ map }, matchIndex, termLength) {
   if (matchIndex < 0 || !map.length) return null;

   const lastNormIndex = Math.min(matchIndex + termLength - 1, map.length - 1);
   const start = map[matchIndex];
   const end = map[lastNormIndex] + 1;

   return { start, end };
}

/**
 * Classifica um item contra o termo já normalizado.
 *
 * @returns {{ rank: number, matchIndex: number, labelRange, pathRange } | null}
 *          null quando o item não casa de forma alguma.
 */
function scoreItem(item, term) {
   const labelIndex = buildNormalizedIndex(item.label);
   const at = labelIndex.norm.indexOf(term);

   if (at >= 0) {
      let rank;
      if (labelIndex.norm === term) rank = RANK_EXACT;
      else if (at === 0) rank = RANK_PREFIX;
      else if (/[^a-z0-9]/.test(labelIndex.norm[at - 1])) rank = RANK_WORD_PREFIX;
      else rank = RANK_CONTAINS;

      return {
         rank,
         matchIndex: at,
         labelRange: toOriginalRange(labelIndex, at, term.length),
         pathRange: null,
      };
   }

   // Não casou o rótulo — ainda pode casar o nome do módulo (§2.3 do handoff).
   const moduleIndex = buildNormalizedIndex(item.moduleName);
   const atModule = moduleIndex.norm.indexOf(term);

   if (atModule >= 0) {
      return {
         rank: RANK_MODULE,
         matchIndex: atModule,
         labelRange: null,
         pathRange: toOriginalRange(moduleIndex, atModule, term.length),
      };
   }

   return null;
}

/**
 * Busca ordenada por relevância, limitada a MAX_RESULTS.
 *
 * @param {Array} flatItems - árvore já filtrada por permissão
 * @param {string} query
 * @returns {{ results: Array, total: number, isActive: boolean }}
 *          `total` é a contagem ANTES do corte — a UI informa quando truncou,
 *          em vez de truncar em silêncio.
 */
export function searchItems(flatItems, query) {
   const term = normalizeText((query || '').trim());

   if (term.length < MIN_QUERY_LENGTH) {
      return { results: [], total: 0, isActive: false };
   }

   const scored = [];

   flatItems.forEach((item) => {
      const score = scoreItem(item, term);
      if (score) scored.push({ ...item, ...score });
   });

   scored.sort(
      (a, b) =>
         a.rank - b.rank ||
         a.matchIndex - b.matchIndex ||
         a.label.length - b.label.length ||
         a.label.localeCompare(b.label, 'pt-BR')
   );

   return {
      results: scored.slice(0, MAX_RESULTS),
      total: scored.length,
      isActive: true,
   };
}
